import { useEffect, useState } from 'react';
import type { ProfessionalAvailability, ProfessionalStatus } from '@jabberr/core';
import { api } from './api.js';
import type { ProfessionalSummary } from '@jabberr/core';
export type { ProfessionalAvailability, ProfessionalStatus, ProfessionalSummary } from '@jabberr/core';

/** Retry transient account checks with a bounded backoff. Never infer access from a saved preference. */
export function useProfessionalAvailability(userId: string | undefined): ProfessionalAvailability | null {
  const [mine, setMine] = useState<{ userId: string; availability: ProfessionalAvailability } | null>(null);
  useEffect(() => {
    let alive = true;
    let retryTimer: number | undefined;
    let failures = 0;
    const retryDelays = [1000, 2000, 5000];
    setMine(null);
    const refresh = async () => {
      if (!userId) return;
      try {
        const availability = await api.professional.availability();
        if (alive) setMine({ userId, availability });
      } catch {
        const delay = retryDelays[failures++];
        if (alive && delay !== undefined) retryTimer = window.setTimeout(() => void refresh(), delay);
      }
    };
    if (userId) void refresh();
    return () => { alive = false; window.clearTimeout(retryTimer); };
  }, [userId]);
  return mine && mine.userId === userId ? mine.availability : null;
}

const preferenceKey = (userId: string) => `jabberr:professional-voice:${userId}`;

/** This is an opt-in on this device, never permission to open a paid session. */
export function professionalPreference(userId: string): boolean {
  try { return localStorage.getItem(preferenceKey(userId)) === 'on'; }
  catch { return false; }
}

export function setProfessionalPreference(userId: string, enabled: boolean): void {
  try { localStorage.setItem(preferenceKey(userId), enabled ? 'on' : 'off'); }
  catch { /* The choice still works for the mounted control. */ }
}

export function professionalRunning(status: ProfessionalStatus | null): boolean {
  return status !== null && ['connecting', 'active', 'closing', 'finalization_unknown'].includes(status.state);
}

export function professionalCost(micros: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(micros / 1_000_000);
}

/** "12 sec", "3 min 05 sec": a session's length in words. */
export function professionalDuration(seconds: number): string {
  if (seconds < 60) return `${seconds} sec`;
  return `${Math.floor(seconds / 60)} min ${String(seconds % 60).padStart(2, '0')} sec`;
}
